import styles from "./templateadd.module.css";
import { Link, useHistory } from "react-router-dom";
import Helmet from "react-helmet";
import { FormControl, IconButton, InputLabel, MenuItem, Select, Slide, Snackbar, TextField, Typography } from "@mui/material";
import { ArrowLeft, DollarSign, Home, X } from "react-feather";
import { Editor } from "@tinymce/tinymce-react";
import Loader from "../../../../../components/Loader";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import Footer from "../../../../../components/Footer";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AddLogCallAPI } from "../../../../../components/AddLogs";
import NumberFormat from "react-number-format";

const MoneyField = (props) => {
    return (<NumberFormat prefix="" displayType="input" type="text" thousandSeparator={true} allowNegative={false} decimalScale={2} fixedDecimalScale    {...props} />);
}

const schema = yup.object().shape({
    title: yup.string().required("This field is Required"),
    rent_adjustment: yup.string().required("This field is Required"),
    credit_amount: yup.string().test("max-4-digit", "Rent can not be more than 6 digits.", (value) => {
        if (!value) return true;

        return parseInt(value?.replace(/,/g, "")) < 10000;
    }),
    day_before: yup.string().required("This field is Required"),
    description: yup.string().required("This field is Required")
});

const TemplateAdd = ({ history, match }) => {
    const { getAccessTokenSilently } = useAuth0();
    const redirect = useHistory();
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const { id } = match.params;
    const isAddMode = !id;
    const editorRef = useRef(null);

    const { errors, control, handleSubmit, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: '',
            day_before: '',
            rent_adjustment: '',
            credit_amount: '',
            description: ''
        }
    });

    useEffect(() => {
        const fetchDetails = async () => {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
            axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/lease-renewal/template/" + id, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setValue('title', response.data.data?.title);
                setValue('day_before', response.data.data?.day_before);
                setValue('rent_adjustment', response.data.data?.rent_adjustment);
                setValue('description', response.data.data?.description);
                if (parseFloat(response.data.data?.credit_amount) > 0) {
                    setValue('credit_amount', response.data.data?.credit_amount);
                }
            }).catch((error) => { });
        }

        if (id) {
            fetchDetails();
        }
    }, [id]);

    const onSubmit = async (data) => {
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        if (data?.credit_amount === "") {
            delete data?.credit_amount;
        } else {
            data = { ...data, credit_amount: data?.credit_amount?.replace(/,/g, "") };
        }

        axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/lease-renewal/template", data, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then((response) => {
            setLoading(false);
            setSnackbarMsg("A new template has been added successfully.");
            setSnackbarOpen(true);

            let logData = {
                title: "A new template has been added successfully.",
                description: [
                    "Name: " + response?.data?.data?.title
                ],
            };
            AddLogCallAPI(logData, token);

            setTimeout(() => {
                redirect.push('/communication/renewal-offers');
            }, 2000);

        }).catch((error) => {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const onUpdate = async (data) => {
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        if (data?.credit_amount === "") {
            delete data?.credit_amount;
        } else {
            data = { ...data, credit_amount: data?.credit_amount?.replace(/,/g, "") };
        }

        axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/lease-renewal/template/" + id, data, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then((response) => {
            setLoading(false);
            setSnackbarMsg("A template has been updated successfully.");
            setSnackbarOpen(true);

            let logData = {
                title: "A template has been updated successfully.",
                description: [
                    "Name: " + response?.data?.data?.title
                ],
            };
            AddLogCallAPI(logData, token);

            setTimeout(() => {
                redirect.push('/communication/renewal-offers');
            }, 2000);

        }).catch((error) => {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<>
        <Helmet title={isAddMode ? "Add Renewal Offer Template" : "Edit Renewal Offer Template"} />

        {loading && <Loader />}

        <div className={`${styles.mainCard}`}>
            <div className={`${styles.SMProCusRow}`}>
                <div className={`${styles.BreadcrumbHeadSec}`}>
                    <nav aria-label="breadcrumb">
                        <ol className={`${styles.breadcrumb}`}>
                            <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Lease Renewal</li>
                            <li className={`${styles.breadcrumbItem}`}>
                                <Link to={"/communication/renewal-offers"}>Offer Template</Link>
                            </li>
                            <li className={`${styles.breadcrumbItem} ${styles.active}`}>{isAddMode ? "Add Offer Template" : "Edit Offer Template"}</li>
                        </ol>
                    </nav>
                    <div className={`${styles.PageTitleSec}`}>
                        <div>
                            <Link to={`/communication/renewal-offers`} className={`${styles.BackArrow}`}><ArrowLeft /></Link>
                            <Typography display="inline" className={`${styles.pageTitle}`}>{isAddMode ? "Add Offer Template" : "Edit Offer Template"}</Typography>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit(isAddMode ? onSubmit : onUpdate)}>
                    <div className={`${styles.EmailTemplateBodySec}`}>
                        <div className={`${styles.AddLeaseSection}`}>
                            <div className={`${styles.AddDocTitleSec}`}>
                                <div className={`${styles.HalfStepFormRow}`}>
                                    <div className={`${styles.TitleFullSec} FormGroup`}>
                                        <Controller
                                            name="title"
                                            control={control}
                                            render={(field) => (
                                                <TextField
                                                    {...field}
                                                    id="outlined-basic"
                                                    variant="outlined"
                                                    label="Title*"
                                                />
                                            )}
                                        />
                                        {errors.title && (<p className={`${styles.ErrorM}`}>{errors.title.message}</p>)}
                                    </div>
                                    <div className={`${styles.TitleDurationSec} FormGroup`}>
                                        <FormControl fullWidth>
                                            <InputLabel id="demo-simple-select-label">Rent Adjustment*</InputLabel>
                                            <Controller
                                                name="rent_adjustment"
                                                control={control}
                                                render={(field) => (
                                                    <Select
                                                        {...field}
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        label="Rent Adjustment*"
                                                    >
                                                        <MenuItem value={1}>No Change</MenuItem>
                                                        <MenuItem value={2}>Increase Rent</MenuItem>
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                        {errors.rent_adjustment && (<p className={`${styles.ErrorM}`}>{errors.rent_adjustment.message}</p>)}
                                    </div>
                                    <div className={`${styles.TitleDurationSec} FormGroup`}>
                                        <FormControl fullWidth>
                                            <InputLabel id="demo-simple-select-label">Lease Expiration*</InputLabel>
                                            <Controller
                                                name="day_before"
                                                control={control}
                                                render={(field) => (
                                                    <Select
                                                        {...field}
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        label="Lease Expiration*"
                                                    >
                                                        <MenuItem value="90">90 Days Before</MenuItem>
                                                        <MenuItem value="60">60 Days Before</MenuItem>
                                                        <MenuItem value="30">30 Days Before</MenuItem>
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                        {errors.day_before && (<p className={`${styles.ErrorM}`}>{errors.day_before.message}</p>)}
                                    </div>
                                    <div className={`${styles.TitleDurationSec} FormGroup CustomWithIcon`}>
                                        <DollarSign className={`${styles.CustomFieldIcon}`} />
                                        <Controller
                                            name="credit_amount"
                                            control={control}
                                            render={(field) => (
                                                <TextField
                                                    {...field}
                                                    id="outlined-basic"
                                                    variant="outlined"
                                                    label="Credit Amount"
                                                    InputProps={{ inputComponent: MoneyField }}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={`${styles.FormGroupTextareaFull}`}>
                                <label className={`${styles.FilterLabel}`}>Description<span className={`${styles.errorSpan}`}>*</span></label>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={(field) => (
                                        <Editor
                                            onInit={(evt, editor) => { editorRef.current = editor }}
                                            className={`${styles.formControlTextArea}`}
                                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                            value={field?.value}
                                            init={{
                                                height: 500,
                                                menubar: false,
                                                plugins: ["lists", "code", "table", "media", "image"],
                                                toolbar: ["undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor removeformat | alignleft aligncenter alignright alignjustify | numlist bullist | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | image | code",
                                                    "tenantname propertyname aptno leaseexipredate offervaliddate acceptlink unsurelink currentrent newrent increasedrent increaseddeposit",
                                                ],
                                                font_size_formats: "10px 12px 14px 16px 18px 20px 24px 36px 48px",
                                                content_style: 'body { font-family:Helvetica,Arial,sans-serif !important; font-size:16px }',
                                                automatic_uploads: true,
                                                file_picker_types: 'image',
                                                images_upload_url: process.env.REACT_APP_USER_API_URL + "v1/campaign/email-templates/upload-image",
                                                setup: (editor) => {
                                                    editor.ui.registry.addButton("tenantname", {
                                                        text: "Tenant Name",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{TenantName}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("propertyname", {
                                                        text: "Property Name",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{PropertyName}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("aptno", {
                                                        text: "Apt No",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{AptNo}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("leaseexipredate", {
                                                        text: "Lease Expire Date",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{LeaseExpireDate}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("offervaliddate", {
                                                        text: "Offer Valid Date",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{OfferValidDate}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("acceptlink", {
                                                        text: "Accept Link",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{AcceptLink}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("unsurelink", {
                                                        text: "Unsure Link",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{UnsureLink}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("currentrent", {
                                                        text: "Current Rent",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{CurrentRent}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("newrent", {
                                                        text: "New Rent",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{NewRent}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("increasedrent", {
                                                        text: "Additional Rent",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{IncreasedRent}}}} `),
                                                    });
                                                    editor.ui.registry.addButton("increaseddeposit", {
                                                        text: "Additional Deposit",
                                                        onAction: () =>
                                                            editor.insertContent(` {{{{IncreasedDeposit}}}} `),
                                                    });
                                                },
                                            }}
                                            onEditorChange={(e) => field.onChange(e)}
                                        />
                                    )}
                                />
                                {errors.description && (<p className={`${styles.ErrorM}`}>{errors.description.message}</p>)}
                            </div>
                            <div className={`${styles.ButtonAction}`}>
                                <button className={`${styles.CancelPopupBU}`} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push('/communication/renewal-offers'); }}>Cancel</button>
                                <button className={`${styles.SubmitPopupBU}`} type="submit">{isAddMode ? 'Add' : 'Update'}</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <Snackbar
            open={snackbarOpen}
            onClose={(e) => setSnackbarOpen(false)}
            TransitionComponent={Slide}
            message={snackbarMsg}
            autoHideDuration={10000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            action={
                <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={(e) => setSnackbarOpen(false)}
                >
                    <X />
                </IconButton>
            }
        />

        <Footer />
    </>)
}

export default withAuthenticationRequired(TemplateAdd, {
    onRedirecting: () => <Loader />,
});